import { Box, Grid } from '@mui/material';
import ResourceCard from './ResourceCard';
import { Resource, ResourceMetadata } from '@esg/esg-global-types';
import moment from 'moment';
import SkeletonGrid, { SkeletonItem } from '../../shared/loading/SkeletonGrid';
import { getMetadataForResource } from '../../../lib/resource_centre/resource_metadata';

interface ResourceCentreDisplayGridProps {
  resources: Array<Resource>;
  resources_metadata: Array<ResourceMetadata>;
  resources_loading: boolean;
  handleUpdateResourcesMetadata: (updated_resource_metadata: ResourceMetadata) => void;
}

/**
 * Styled display grid for uniform display of Resource Centre Resources.
 * @param {Array<Resource>} resources List of filtered resources to display from parent.
 * @param {Array<ResourceMetadata>} resources_metadata List of company level resource metadata objects corresponding to list of resources.
 * @param {boolean} resources_loading Flag for resource data still loading from parent to show skeleton feedback of grid.
 * @param {(updated_resource_metadata: ResourceMetadata): void} handleUpdateResourcesMetadata Update resource metadata in parent from downloading resource files.
 * @returns {JSX.Element}
 */
const ResourceCentreDisplayGrid = ({
  resources,
  resources_metadata,
  resources_loading,
  handleUpdateResourcesMetadata
}: ResourceCentreDisplayGridProps) => {
  // Order displayed resources by created date (newest first).
  const sorted_resources: Array<Resource> = resources.sort(
    (a: Resource, b: Resource) => moment(b.created).unix() - moment(a.created).unix()
  );

  // Populate an array of skeleton items to be displayed while loading.
  const skeleton_grid_items: Array<SkeletonItem> = Array(5).fill({
    size: 2.4,
    height: 450,
    variant: 'rounded'
  });

  return (
    <Box>
      <Grid container spacing={3} sx={{ my: 3 }}>
        {resources_loading ? (
          <SkeletonGrid skeleton_items={skeleton_grid_items} />
        ) : (
          sorted_resources.map((resource: Resource) => (
            <Grid size={2.4} key={resource.id}>
              <ResourceCard
                resource={resource}
                resource_metadata={getMetadataForResource(resource.id, resources_metadata)}
                handleUpdateResourcesMetadata={handleUpdateResourcesMetadata}
              />
            </Grid>
          ))
        )}
      </Grid>
    </Box>
  );
};

export default ResourceCentreDisplayGrid;
